/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import mapboxgl, { type Map, type MapMouseEvent } from 'mapbox-gl';

import { useBreakpoint } from 'sarsaparilla';

import { convertToGeoJson } from './convertToGeoJson';
import { useMap, type FitBoundsToMarkersOptions } from './MapContext';
import { circleIconsColors, defaultPadding, mobilePadding } from './map.constants';

import type { MapMarkerItem } from './map.types';

const MARKERS_SOURCE_ID = 'markers';
const PIN_LAYER_ID = 'pin-layer';
const CIRCLE_LAYER_ID = 'circle-layer';
const PIN_HOVER_LAYER_ID = 'pin-hover-layer';
const CIRCLE_HOVER_SHADOW_LAYER_ID = 'circle-hover-layer-shadow';
const CIRCLE_HOVER_LAYER_ID = 'circle-hover-layer';

const CIRCLE_PIN_ZOOM_TRANSITION = 10;
const FEW_MARKERS_MAX_ZOOM = CIRCLE_PIN_ZOOM_TRANSITION + 1;
const MANY_MARKERS_MAX_ZOOM = 16;
const FEW_MARKERS_THRESHOLD = 3;
const CIRCLE_FALLBACK_COLOR = '#4a77b4';

export interface UseMapMarkersParams<T extends MapMarkerItem> {
    data?: readonly T[];
    onMarkerClick: (id: string | null) => void;
    /** When set, the matching marker gets the `-active` sprite variant. */
    activeCardId?: string | null;
    /**
     * When set (and no `activeCardId` is active), the matching marker gets
     * the `-active` sprite variant to reflect card-list hover.
     */
    hoveredCardId?: string | null;
    /**
     * Called immediately before every programmatic fit the hook performs
     * (initial-data fit, resize fit, and provider-triggered fit). Consumers
     * that run their own logic on `moveend` (e.g. search-on-map-move) can use
     * this to flag and ignore the `moveend` that a programmatic camera move
     * produces, so it isn't mistaken for a user gesture.
     */
    onBeforeFitBounds?: () => void;
}

export interface UseMapMarkersResult {
    fitBoundsToMarkers: (options?: FitBoundsToMarkersOptions) => void;
}

/**
 * Manages the shared map's marker source, symbol/circle layers, click and
 * cursor interactions, and bounds fitting. Must be used inside a
 * `MapProvider`. The hook also registers its `fitBoundsToMarkers` with the
 * provider so sibling components can trigger a re-fit via `useMap()`.
 */
export function useMapMarkers<T extends MapMarkerItem>({
    data,
    onMarkerClick,
    activeCardId,
    hoveredCardId,
    onBeforeFitBounds,
}: UseMapMarkersParams<T>): UseMapMarkersResult {
    const { mapInstance, registerFitBoundsToMarkers } = useMap();
    const { isMdAndDown } = useBreakpoint();

    const [hasSetDataSource, setHasSetDataSource] = React.useState(false);
    const didAddLayersRef = React.useRef(false);
    const mapInstanceRef = React.useRef<Map | null>(null);

    // If the map instance changes (unmount/remount, HMR), reset our "did we
    // already set up X" flags so the next map gets its own source and layers.
    React.useEffect(() => {
        if (mapInstance !== mapInstanceRef.current) {
            mapInstanceRef.current = mapInstance;
            didAddLayersRef.current = false;
            setHasSetDataSource(false);
        }
    }, [mapInstance]);

    // Active takes precedence over hover: if a popup/card is "open", we do
    // not want card-list hover to steal the -active suffix.
    const highlightId = activeCardId ?? hoveredCardId ?? null;

    const markersGeojson = React.useMemo(
        () => convertToGeoJson(data, highlightId),
        [data, highlightId]
    );

    // Create the GeoJSON source (once) and keep it in sync with the data.
    React.useEffect(() => {
        if (!mapInstance) return;
        if (isMapRemoved(mapInstance)) return;

        const existing = mapInstance.getSource(MARKERS_SOURCE_ID);
        if (existing && 'setData' in existing && typeof existing.setData === 'function') {
            // Applying new data mid-gesture (e.g. while a pan-triggered fetch
            // resolves during the pan) fights the in-flight camera animation
            // and can visibly reset markers. If the map is moving, defer the
            // update until it settles; otherwise apply immediately.
            if (isMapMoving(mapInstance)) {
                const applyWhenIdle = () => {
                    mapInstance.off('moveend', applyWhenIdle);
                    if (isMapRemoved(mapInstance)) return;
                    const source = mapInstance.getSource(MARKERS_SOURCE_ID);
                    if (
                        source &&
                        'setData' in source &&
                        typeof source.setData === 'function'
                    ) {
                        source.setData(markersGeojson);
                    }
                };
                mapInstance.on('moveend', applyWhenIdle);
                return () => {
                    mapInstance.off('moveend', applyWhenIdle);
                };
            }

            existing.setData(markersGeojson);
            return;
        }

        mapInstance.addSource(MARKERS_SOURCE_ID, {
            type: 'geojson',
            data: markersGeojson,
            promoteId: 'id',
        });

        setHasSetDataSource(true);
        return undefined;
    }, [mapInstance, markersGeojson]);

    // Add the five rendering layers once the source is ready.
    React.useEffect(() => {
        if (!mapInstance) return;
        if (didAddLayersRef.current) return;
        if (!hasSetDataSource) return;
        if (isMapRemoved(mapInstance)) return;
        if (!mapInstance.getSource(MARKERS_SOURCE_ID)) return;

        mapInstance.addLayer({
            id: PIN_LAYER_ID,
            source: MARKERS_SOURCE_ID,
            type: 'symbol',
            minzoom: CIRCLE_PIN_ZOOM_TRANSITION,
            layout: {
                'icon-allow-overlap': true,
                'icon-image': ['concat', ['get', 'icon'], ['get', 'suffix']],
                'symbol-sort-key': ['to-number', ['get', 'priority']],
                'icon-size': 0.9,
                'icon-anchor': 'bottom',
                'icon-offset': [0, 20],
            },
        });

        mapInstance.addLayer({
            id: CIRCLE_LAYER_ID,
            source: MARKERS_SOURCE_ID,
            type: 'circle',
            maxzoom: CIRCLE_PIN_ZOOM_TRANSITION,
            paint: {
                'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 6.5, 10, 9],
                'circle-stroke-width': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    8,
                    1.5,
                    10,
                    2.25,
                ],
                'circle-color': buildCircleColorExpression(),
                'circle-stroke-color': '#fff',
            },
        });

        mapInstance.addLayer({
            id: PIN_HOVER_LAYER_ID,
            source: MARKERS_SOURCE_ID,
            type: 'symbol',
            minzoom: CIRCLE_PIN_ZOOM_TRANSITION,
            layout: {
                'icon-allow-overlap': true,
                'icon-image': ['concat', ['get', 'icon'], ['get', 'suffix']],
                'symbol-sort-key': ['to-number', ['get', 'priority']],
                'icon-size': 1.1,
                'icon-anchor': 'bottom',
                'icon-offset': [0, 17],
            },
            filter: ['case', ['==', ['get', 'suffix'], '-active'], true, false],
        });

        mapInstance.addLayer({
            id: CIRCLE_HOVER_SHADOW_LAYER_ID,
            source: MARKERS_SOURCE_ID,
            type: 'circle',
            maxzoom: CIRCLE_PIN_ZOOM_TRANSITION,
            filter: ['case', ['==', ['get', 'suffix'], '-active'], true, false],
            paint: {
                'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 18, 10, 20],
                'circle-color': '#000',
                'circle-blur': 0.5,
                'circle-opacity': 0.3,
                'circle-translate': [0, 2],
            },
        });

        mapInstance.addLayer({
            id: CIRCLE_HOVER_LAYER_ID,
            source: MARKERS_SOURCE_ID,
            type: 'circle',
            maxzoom: CIRCLE_PIN_ZOOM_TRANSITION,
            filter: ['case', ['==', ['get', 'suffix'], '-active'], true, false],
            paint: {
                'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 11, 10, 12],
                'circle-stroke-width': 3,
                'circle-color': '#A64526',
                'circle-stroke-color': '#fff',
            },
        });

        didAddLayersRef.current = true;
    }, [mapInstance, hasSetDataSource]);

    // Click + cursor interactions on the two hit-testable layers.
    React.useEffect(() => {
        if (!mapInstance) return;
        if (isMapRemoved(mapInstance)) return;

        const handleClick = (event: MapMouseEvent) => {
            const features = mapInstance.queryRenderedFeatures(event.point, {
                layers: [PIN_LAYER_ID, CIRCLE_LAYER_ID],
            });

            if (features.length === 0) {
                onMarkerClick(null);
                return;
            }

            const topFeature = features[features.length - 1];
            const featureId = topFeature?.id;
            onMarkerClick(typeof featureId === 'string' ? featureId : null);
        };

        const setPointer = () => {
            mapInstance.getCanvas().style.cursor = 'pointer';
        };
        const clearPointer = () => {
            mapInstance.getCanvas().style.cursor = '';
        };

        mapInstance.on('click', handleClick);
        mapInstance.on('mouseenter', PIN_LAYER_ID, setPointer);
        mapInstance.on('mouseleave', PIN_LAYER_ID, clearPointer);
        mapInstance.on('mouseenter', CIRCLE_LAYER_ID, setPointer);
        mapInstance.on('mouseleave', CIRCLE_LAYER_ID, clearPointer);

        return () => {
            mapInstance.off('click', handleClick);
            mapInstance.off('mouseenter', PIN_LAYER_ID, setPointer);
            mapInstance.off('mouseleave', PIN_LAYER_ID, clearPointer);
            mapInstance.off('mouseenter', CIRCLE_LAYER_ID, setPointer);
            mapInstance.off('mouseleave', CIRCLE_LAYER_ID, clearPointer);
        };
    }, [mapInstance, onMarkerClick]);

    const fitBoundsToMarkers = React.useCallback(
        (options?: FitBoundsToMarkersOptions) => {
            const { speed = 1.25, maxDuration = 4000 } = options ?? {};
            if (!mapInstance) return;
            if (!data?.length) return;
            if (isMapRemoved(mapInstance)) return;

            onBeforeFitBounds?.();

            try {
                const bounds = new mapboxgl.LngLatBounds();
                data.forEach((d) => {
                    const lng = parseFloat(d.longitude);
                    const lat = parseFloat(d.latitude);
                    if (Number.isNaN(lng) || Number.isNaN(lat)) return;
                    bounds.extend([lng, lat]);
                });

                const fitOptions: mapboxgl.FitBoundsOptions = {
                    padding: isMdAndDown ? mobilePadding : defaultPadding,
                    speed,
                    maxDuration,
                    offset: [0, 0],
                };

                // For a tiny set of markers we cap the zoom lower so the
                // camera keeps some surrounding context. If the user has
                // already zoomed in further than our cap, honor their zoom.
                const idealMaxZoom =
                    data.length < FEW_MARKERS_THRESHOLD
                        ? FEW_MARKERS_MAX_ZOOM
                        : MANY_MARKERS_MAX_ZOOM;
                if (mapInstance.getZoom() < idealMaxZoom) {
                    fitOptions.maxZoom = idealMaxZoom;
                }

                mapInstance.fitBounds(bounds, fitOptions);
            } catch {
                // Swallow: Mapbox can throw 'Invalid LngLat object' during
                // desktop→mobile resize races between DOM removal and the
                // resize listener. See the legacy hook for context.
            }
        },
        [data, mapInstance, isMdAndDown, onBeforeFitBounds]
    );

    // Publish our implementation to the provider so siblings can call it via
    // `useMap().fitBoundsToMarkers`.
    React.useEffect(() => {
        registerFitBoundsToMarkers(fitBoundsToMarkers);
    }, [registerFitBoundsToMarkers, fitBoundsToMarkers]);

    // Fit on initial data availability and whenever the map resizes.
    const didFitInitialRef = React.useRef(false);
    React.useEffect(() => {
        if (!mapInstance) return;
        if (!data?.length) return;
        if (isMapRemoved(mapInstance)) return;

        if (!didFitInitialRef.current) {
            fitBoundsToMarkers();
            didFitInitialRef.current = true;
        }

        const onResize = () => fitBoundsToMarkers();
        mapInstance.on('resize', onResize);
        return () => {
            mapInstance.off('resize', onResize);
        };
    }, [mapInstance, data, fitBoundsToMarkers]);

    return React.useMemo(() => ({ fitBoundsToMarkers }), [fitBoundsToMarkers]);
}

/**
 * Builds the Mapbox `match` expression that maps `icon + suffix` to the
 * corresponding circle color, plus a fallback. Both empty-suffix and
 * `-disabled`/`-active` variants share the same base color when only the
 * empty variant is defined in `circleIconsColors`.
 */
function buildCircleColorExpression(): mapboxgl.Expression {
    const flat: (string | mapboxgl.Expression)[] = [];
    circleIconsColors.forEach(([icon, color]) => {
        flat.push(icon, color);
    });
    return [
        'match',
        ['concat', ['get', 'icon'], ['get', 'suffix']],
        ...flat,
        CIRCLE_FALLBACK_COLOR,
    ] as unknown as mapboxgl.Expression;
}

function isMapRemoved(map: Map): boolean {
    // `_removed` is an internal Mapbox flag that guards against operating on
    // a torn-down map. The legacy hook relies on it for HMR stability.
    return (map as unknown as { _removed?: boolean })._removed === true;
}

function isMapMoving(map: Map): boolean {
    // `isMoving()` reports whether a pan/zoom/rotate animation is in flight.
    // Guard the call since not every (mocked) map implements it.
    const candidate = map as unknown as { isMoving?: () => boolean };
    return typeof candidate.isMoving === 'function' ? candidate.isMoving() : false;
}

// cSpell:ignore minzoom, maxzoom, styledata
